import apiUrl from './api';
import { getWithAuth } from '@/lib/httpAdapter';

/**
 * Patterns-chart day drill-down (ENT-4525) — one member's per-session ledger
 * aggregates and itemized per-request billing rows for a single UTC day.
 *
 *   GET /coding-agent/day-drill-down/?user_uuid=&day= -> { success, day,
 *     user_uuid, sessions, requests, truncated }
 *
 * Org is derived from the caller's token, same Bearer-JWT + `{ success: true,
 * ... }` envelope as every other coding-agent/* route.
 */

/** One request-drawer row's exact per-million rate at the time it was
 *  charged -- display-only, mirrors TaskRequestRateLines in
 *  codingAgentThemes.ts (ENT-4523). Absent on a vendor (Cursor) row, or when
 *  the fact's stamped rate-card version no longer matches the current one. */
export interface DayDrillDownRateLines {
  input: number;
  cache_read: number;
  cache_write_5m: number;
  cache_write_1h: number;
  output: number;
}

/** One billed session's own ledger aggregate for this day -- never resummed
 *  from `requests` below (the backend deliberately keeps the two sections
 *  independent; see the day-drill-down decision in the backend's
 *  knowledge base). */
export interface DayDrillDownSession {
  session_id: string;
  agent: string | null;
  /** Null when the day node stored no cost at all -- distinct from an
   *  actual $0 charge. */
  cost_cents: number | null;
  requests: number;
  input_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  output_tokens: number;
  cost_basis: string | null;
  /** Per-model usage map for this session's day, keyed by model id. */
  models: Record<string, unknown>;
  /** The v14/v15 identity fact's own title (ENT-4702 follow-up) -- the SAME
   *  field `sessionIdentity.nativeSessionTitle` surfaces on the Sessions-tab
   *  list, so a task-less session (task extraction routinely lags raw sync
   *  by days) can still show its real title here instead of a repo/project
   *  guess. `null` when no identity has landed for this session. */
  native_session_title: string | null;
  /** Off the session's own graph node (ENT-4702) -- the NEXT title fallback,
   *  after `native_session_title` and task names, for a session with
   *  neither. Both `null` when the session stored neither. */
  repo_name: string | null;
  project_name: string | null;
  tasks: { task_id: string; name: string | null }[];
}

/** One billing request inside a member's day, resolved onto a task when its
 *  turn falls inside one of that session's tasks (null otherwise -- untasked
 *  spend is real and is never guessed onto a task). */
export interface DayDrillDownRequest {
  fact_id: string;
  session_id: string;
  event_at: string;
  turn_id: string | null;
  task_id: string | null;
  task_name: string | null;
  model: string | null;
  input_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  cache_write_1h_tokens: number;
  output_tokens: number;
  cost_cents: number | null;
  cost_basis: 'vendor' | 'rate_card' | string;
  vendor: Record<string, unknown>;
  rate_lines?: DayDrillDownRateLines;
}

export interface DayDrillDownResponse {
  success: boolean;
  day: string;
  user_uuid: string;
  sessions: DayDrillDownSession[];
  requests: DayDrillDownRequest[];
  /** True when the day's requests exceeded the endpoint's row cap (1000) --
   *  a mega-session-dump guard, not a limit any real day hits. */
  truncated: boolean;
}

// getWithAuth applies its own 10-minute timeout; race a tighter one so a
// hung/slow backend surfaces as an error state in ~25s, not minutes of skeleton.
const TIMEOUT_MS = 25_000;
function withTimeout<T>(p: Promise<T>, label: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out`)), TIMEOUT_MS)
    ),
  ]);
}

/** One member's day breakdown for the Patterns chart's "explain" affordance
 *  (ENT-4525). The endpoint 404s only when the member billed nothing this
 *  org on this day -- a day that billed exactly $0 still returns session
 *  rows, not a 404. An activity-only `patternsDaily` point (task-active but
 *  never ledger-billed) is a real, expected case of this -- so a 404 here is
 *  folded into an empty result rather than thrown, letting `DayDrillDownModal`
 *  render its existing "No billed sessions on this day" empty state instead
 *  of a load-error message for a perfectly valid day. */
export const fetchDayDrillDown = async (
  userUuid: string,
  day: string
): Promise<DayDrillDownResponse> => {
  const qs = new URLSearchParams({ user_uuid: userUuid, day });
  const r = await withTimeout(
    getWithAuth(`${apiUrl}coding-agent/day-drill-down/?${qs.toString()}`),
    'coding-agent/day-drill-down'
  );
  if (r.status === 404) {
    return {
      success: true,
      day,
      user_uuid: userUuid,
      sessions: [],
      requests: [],
      truncated: false,
    };
  }
  if (!r.ok) throw new Error(`coding-agent/day-drill-down failed: ${r.status}`);
  return r.json();
};
